/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  Stack,
  Typography,
  InputAdornment,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CommonDialog from "../CommonDialog";
import { selectActiveSession } from "app/modules/schools/selectors";
import { useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import StudentCard from "../ledger/books/components/StudentCard";
import TextFieldLabel from "../../TextFieldLabel";
import {
  CustomAutoCompleteController,
  CustomSelectBox,
  CustomTextField,
} from "assets/styles/globalStyledComponent";
import { useMutation, useQuery } from "react-query";
import {
  getAllRouteList,
  getRangeDetail,
  getTransportationFeeStructure,
  getTransportFeeStructureByRange,
  updateTransportInfoInStudentProfile,
  getStops,
  getStopByName,
} from "app/services/schoolService/transport.service";
import { toast } from "react-toastify";
import { IndianCurrency } from "app/utils/helper";
import { ArrowRight, East } from "@mui/icons-material";
import StudentSearchDialogForSiblings from "app/modules/admissionEnquiry/components/enquirypage/StudentSearchDialogForSiblings";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { LoadingButton } from "@mui/lab";
import CustomLinearProgress from "../../CustomLinearProgress";
import useDebounce from "app/hooks/globallyApplicable/useDebounce";

const AddTransportStudentMap = ({
  refetch,
  dialog = false,
  onClose = () => {},
  editData = {},
  showStudentSearch = true,
}) => {
  const selectedSession = useSelector(selectActiveSession);
  const [state, setState] = useState({
    byNameStudentData: {},
    transportationFeeList: [],
    selectedRouteData: {},
    rangeDetailList: [],
    feeStructureList: [],
    stopSearchText: "",
    stopSearchResults: [],
    selectedStopData: null,
    stopSearchLoading: false,
  });

  const debouncedStopSearch = useDebounce(state.stopSearchText, 600);

  // Only use searchFeeBy for radio selection
  const [searchFeeBy, setSearchFeeBy] = useState("feeStructure"); // or "range"

  const initialState = {
    studentDocId: "",
    session: selectedSession,
    using: "No",
    transportFeeDocId: "",
    startDate: new Date(),
    feeAmount: null,
    routeNumber: null,
    routeDocId: "",
    rangeDocId: "",
    to: "",
    vehicleNumber: null,
    remarks: "",
  };

  const {
    control,
    watch,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: initialState,
  });

  // Fetch all routes for 'By Range' mode
  const {
    data: routeResponse,
    isLoading: getAllRouteListLoading,
    isFetching: getAllRouteListFetching,
  } = useQuery({
    queryFn: () =>
      getAllRouteList({
        session: selectedSession,
      }),
    queryKey: ["getAllRouteList", selectedSession],
    enabled: dialog && searchFeeBy === "range",
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
  });

  // Fetch range details based on selected route
  const {
    isLoading: getRangeDetailLoading,
    isFetching: getRangeDetailFetching,
  } = useQuery({
    queryFn: () =>
      getRangeDetail({
        session: selectedSession,
        routeDocId: watch("routeDocId"),
      }),
    queryKey: ["getRangeDetail", watch("routeDocId"), selectedSession],
    // Enable only when dialog is open, By Range is selected, and routeDocId is set
    enabled: dialog && searchFeeBy === "range" && !!watch("routeDocId"),
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    onSuccess: (res) => {
      setState((prev) => ({
        ...prev,
        rangeDetailList: res?.data?.list ?? [],
      }));
    },
  });

  // Fetch transportation fee structure based on selected range
  const {
    data: transportationFeeResponse,
    isLoading: getTransportFeeStructureByRangeLoading,
    isFetching: getTransportFeeStructureByRangeFetching,
  } = useQuery({
    queryFn: () =>
      getTransportFeeStructureByRange({
        session: selectedSession,
        rangeDocId: watch("rangeDocId"),
      }),
    queryKey: [
      "getTransportFeeStructureByRange",
      selectedSession,
      watch("rangeDocId"),
    ],
    // Enable only when By Range is selected, and both routeDocId and rangeDocId are set
    enabled:
      searchFeeBy === "range" && !!watch("routeDocId") && !!watch("rangeDocId"),
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
  });

  // Fetch all fee structures for 'By Fee Structure' mode
  useQuery({
    queryFn: () => getTransportationFeeStructure({ session: selectedSession }),
    queryKey: ["getTransportationFeeStructure", selectedSession],
    enabled:
      dialog && searchFeeBy === "feeStructure" && watch("using") === "Yes",
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    onSuccess: (res) => {
      setState((prev) => ({
        ...prev,
        transportationFeeList: res?.data?.list ?? [],
      }));
    },
  });

  // Fetch stops for selected range
  const { data: stopsResponse } = useQuery({
    queryKey: ["getStops", watch("rangeDocId"), selectedSession],
    queryFn: () =>
      getStops({
        rangeDocId: watch("rangeDocId"),
        session: selectedSession,
      }),
    enabled:
      dialog &&
      searchFeeBy === "range" &&
      watch("using") === "Yes" &&
      !!watch("rangeDocId"),
    onError: (err) => toast.error(err?.response?.data?.message),
  });

  // Search stops by name for 'By Stops' mode
  useQuery({
    queryKey: ["getStopByName", debouncedStopSearch, selectedSession],
    queryFn: () =>
      getStopByName({
        session: selectedSession,
        stopName: debouncedStopSearch,
      }),
    enabled:
      dialog &&
      searchFeeBy === "stops" &&
      !!debouncedStopSearch &&
      debouncedStopSearch.length >= 2,
    onError: (err) => {
      toast.error(err?.response?.data?.message);
      setState((prev) => ({
        ...prev,
        stopSearchLoading: false,
        stopSearchResults: [],
      }));
    },
    onSuccess: (res) => {
      setState((prev) => ({
        ...prev,
        stopSearchResults: res?.data?.list ?? [],
        stopSearchLoading: false,
      }));
    },
  });

  const { mutate, isLoading: updatingTranpsortInfoLoading } = useMutation(
    updateTransportInfoInStudentProfile,
    {
      onSuccess: ({ data }) => {
        toast.success(data?.message);
        reset(initialState);
        setState((prev) => ({
          ...prev,
          byNameStudentData: {},
        }));
        refetch();
        onClose();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message);
      },
    }
  );

  // Set selectedRouteData when route changes (for ByRange)
  useEffect(() => {
    if (searchFeeBy === "range") {
      setState((prev) => ({
        ...prev,
        selectedRouteData:
          routeResponse?.data?.list?.find(
            (item) => item?.routeNumber === watch("routeNumber")
          ) || {},
      }));
    }
  }, [
    routeResponse?.data?.list,
    watch("routeDocId"),
    watch("rangeDocId"),
    searchFeeBy,
  ]);

  // Set feeAmount in form state for ByFeeStructure or ByRange
  useEffect(() => {
    if (searchFeeBy === "feeStructure" && watch("transportFeeDocId")) {
      const feeData = state?.transportationFeeList?.find(
        (item) => item?._id === watch("transportFeeDocId")
      );
      reset((prev) => ({
        ...prev,
        feeAmount: feeData?.feeAmount,
      }));
    }
    if (searchFeeBy === "range" && watch("transportFeeDocId")) {
      const feeData = state?.feeStructureList?.find(
        (item) => item?.feeStructure?._id === watch("transportFeeDocId")
      );
      reset((prev) => ({
        ...prev,
        feeAmount: feeData?.feeStructure?.feeAmount,
      }));
    }
  }, [watch("transportFeeDocId"), searchFeeBy]);

  // Ensure feeAmount is set in form state for ByRange mode
  useEffect(() => {
    if (searchFeeBy === "range" && watch("using") === "Yes") {
      const feeObj = state.feeStructureList?.[0];
      if (
        watch("transportFeeDocId") &&
        feeObj &&
        feeObj._id === watch("transportFeeDocId")
      ) {
        reset((prev) => ({
          ...prev,
          feeAmount: feeObj.feeAmount,
        }));
      } else {
        reset((prev) => ({
          ...prev,
          feeAmount: null,
        }));
      }
    }
  }, [
    searchFeeBy,
    watch("transportFeeDocId"),
    state.feeStructureList,
    watch("using"),
  ]);

  // Set searchFeeBy and form state on edit
  useEffect(() => {
    if (dialog) {
      const isEdit = editData && Object.keys(editData).length > 0;
      if (isEdit) {
        if (editData?.routeNumber) setSearchFeeBy("range");
        else if (editData?.transportFeeDocId) setSearchFeeBy("feeStructure");
        reset((prev) => ({
          ...prev,
          studentDocId: editData?.studentDocId?._id,
          session: selectedSession,
          using: editData?.using,
          routeNumber: editData?.routeNumber,
          routeDocId: editData?.routeDocId,
          rangeDocId: editData?.rangeDocId,
          transportFeeDocId: editData?.transportFeeDocId,
          feeAmount: editData?.feeAmount,
          to: editData?.to,
          vehicleNumber: editData?.vehicleNumber,
          startDate: editData?.startDate
            ? new Date(editData?.startDate)
            : new Date(),
          remarks: editData?.remarks,
        }));
        setState((prev) => ({
          ...prev,
          byNameStudentData: editData?.studentDocId,
          selectedRouteData:
            routeResponse?.data?.list?.find(
              (item) => item?.routeNumber === editData?.routeNumber
            ) || {},
        }));
      } else {
        // new add -> default to stops
        setSearchFeeBy("stops");
        reset(initialState);
        setState((prev) => ({
          ...prev,
          byNameStudentData: {},
          selectedRouteData: {},
        }));
      }
    }
  }, [dialog, editData, routeResponse?.data?.list, selectedSession]);

  useEffect(() => {
    // For ByRange mode, always expect a single obj from API
    if (transportationFeeResponse?.data?.obj) {
      setState((prev) => ({
        ...prev,
        feeStructureList: [transportationFeeResponse.data.obj],
      }));
    } else {
      setState((prev) => ({
        ...prev,
        feeStructureList: [],
      }));
    }
  }, [transportationFeeResponse]);

  // Reset "to" field when range changes
  useEffect(() => {
    if (searchFeeBy === "range") {
      reset((prev) => ({
        ...prev,
        to: "",
      }));
    }
  }, [watch("rangeDocId"), searchFeeBy]);

  // Reset range-related fields when route changes
  useEffect(() => {
    if (searchFeeBy === "range") {
      reset((prev) => ({
        ...prev,
        rangeDocId: "",
        to: "",
        transportFeeDocId: "",
        feeAmount: null,
      }));
      // Clear range detail list and fee structure list
      setState((prev) => ({
        ...prev,
        rangeDetailList: [],
        feeStructureList: [],
      }));
    }
  }, [watch("routeDocId"), searchFeeBy]);

  // Reset stop search when switching modes or dialog closes
  useEffect(() => {
    if (!dialog || searchFeeBy !== "stops") {
      setState((prev) => ({
        ...prev,
        stopSearchText: "",
        stopSearchResults: [],
        selectedStopData: null,
        stopSearchLoading: false,
      }));
    }
  }, [dialog, searchFeeBy]);

  // Set loading state for stop search
  useEffect(() => {
    if (debouncedStopSearch && debouncedStopSearch.length >= 2) {
      setState((prev) => ({
        ...prev,
        stopSearchLoading: true,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        stopSearchResults: [],
        stopSearchLoading: false,
      }));
    }
  }, [debouncedStopSearch]);

  // Auto-set form values when stop is selected in "By Stops" mode
  useEffect(() => {
    if (
      searchFeeBy === "stops" &&
      state.selectedStopData &&
      watch("using") === "Yes"
    ) {
      reset((prev) => ({
        ...prev,
        transportFeeDocId:
          state.selectedStopData.feeStructureObject?.transportFeeDocId,
        feeAmount: state.selectedStopData.feeStructureObject?.feeAmount,
        routeNumber: state.selectedStopData.rangeObject?.routeNumber,
        vehicleNumber: state.selectedStopData.vehicleObject?.vehicleNumber,
        to: state.selectedStopData.stopName,
      }));
    }
  }, [searchFeeBy, state.selectedStopData, watch("using")]);

  const ByFeeStructure = () => {
    return (
      <>
        <Grid item xs={6} md={6}>
          <TextFieldLabel title={"Using"} required />
          <CustomAutoCompleteController
            name={"using"}
            control={control}
            options={["Yes", "No"]}
            placeholder={"Select Using"}
            rules={{
              required: "This field is required",
            }}
          />
        </Grid>
        {watch("using") === "Yes" && (
          <>
            <Grid item xs={6} md={6}>
              <TextFieldLabel
                title={"Fee Structure"}
                required={watch("using") === "Yes"}
              />
              <Controller
                name={"transportFeeDocId"}
                control={control}
                rules={{
                  required:
                    watch("using") === "Yes" ? "This field is required" : false,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <CustomSelectBox
                    native
                    displayEmpty
                    fullWidth
                    size="small"
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                    error={!!errors?.transportFeeDocId}
                  >
                    <MenuItem value="" component="option">
                      Select
                    </MenuItem>
                    {state?.transportationFeeList?.map((item) => (
                      <MenuItem
                        key={item?._id}
                        value={item?._id}
                        component="option"
                      >
                        {item?.feeCode} - {`${IndianCurrency(item?.feeAmount)}`}
                      </MenuItem>
                    ))}
                  </CustomSelectBox>
                )}
              />
            </Grid>

            <Grid item xs={6} md={6}>
              <TextFieldLabel title={"Remarks"} />
              <Controller
                name={"remarks"}
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <CustomTextField
                    fullWidth
                    size="small"
                    onBlur={onBlur}
                    onChange={onChange}
                    placeholder="Type Remarks"
                    value={value}
                  />
                )}
              />
            </Grid>
          </>
        )}
        <Grid item xs={6} md={6}>
          <TextFieldLabel
            title={"Start Date"}
            required={watch("using") === "Yes"}
          />
          <Controller
            name={"startDate"}
            control={control}
            rules={{
              required:
                watch("using") === "Yes" ? "This field is required" : false,
            }}
            render={({ field: { onChange, value } }) => (
              <DesktopDatePicker
                inputFormat="DD MMM, YYYY"
                onChange={onChange}
                value={value}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    size="small"
                    fullWidth
                    error={!!errors?.startDate}
                  />
                )}
              />
            )}
          />
        </Grid>
      </>
    );
  };

  const ByRange = () => {
    return (
      <>
        <CustomLinearProgress
          loading={
            getAllRouteListLoading ||
            getAllRouteListFetching ||
            getRangeDetailLoading ||
            getRangeDetailFetching ||
            getTransportFeeStructureByRangeLoading ||
            getTransportFeeStructureByRangeFetching
          }
        />
        <Grid item xs={12} md={3}>
          <TextFieldLabel title={"Using"} required />
          <CustomAutoCompleteController
            name={"using"}
            control={control}
            options={["Yes", "No"]}
            placeholder={"Select Using"}
            rules={{
              required: "This field is required",
            }}
          />
        </Grid>
        {watch("using") === "Yes" && (
          <>
            <Grid item xs={12} md={3}>
              <TextFieldLabel title="Route Number (Route Name)" required />
              <Controller
                name="routeDocId"
                control={control}
                rules={{
                  required: "This field is required",
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <CustomSelectBox
                    native
                    displayEmpty
                    fullWidth
                    size="small"
                    onBlur={onBlur}
                    onChange={(e) => {
                      onChange(e);
                      // Set selectedRouteData in state
                      const selected = routeResponse?.data?.list?.find(
                        (item) => item?._id === e.target.value
                      );
                      setState((prev) => ({
                        ...prev,
                        selectedRouteData: selected || {},
                        rangeDetailList: [], // Clear range options
                        feeStructureList: [], // Clear fee structure
                      }));
                      // Also set routeNumber in form state for payload and clear range-related fields
                      reset((prev) => ({
                        ...prev,
                        routeNumber: selected?.routeNumber || "",
                        rangeDocId: "", // Clear range selection
                        to: "", // Clear stop selection
                        transportFeeDocId: "", // Clear fee structure
                        feeAmount: null, // Clear fee amount
                      }));
                    }}
                    value={value}
                  >
                    <MenuItem value="" component="option">
                      Select
                    </MenuItem>
                    {routeResponse?.data?.list?.map((item) => (
                      <MenuItem
                        key={item?._id}
                        value={item?._id}
                        component="option"
                      >
                        {item?.routeNumber} ({item?.routeName})
                      </MenuItem>
                    ))}
                  </CustomSelectBox>
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextFieldLabel title="Range" required />
              <Controller
                name="rangeDocId"
                control={control}
                rules={{
                  required: "This field is required",
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <CustomSelectBox
                    native
                    displayEmpty
                    fullWidth
                    size="small"
                    onBlur={onBlur}
                    onChange={(e) => {
                      onChange(e);
                      const selectedRange = state.rangeDetailList.find(
                        (item) => item._id === e.target.value
                      );
                      reset((prev) => ({
                        ...prev,
                        rangeCode: selectedRange?.rangeCode || "",
                      }));
                    }}
                    value={value}
                  >
                    <MenuItem value="" component="option">
                      Select
                    </MenuItem>
                    {state?.rangeDetailList?.map((item) => (
                      <MenuItem
                        key={item?._id}
                        value={item?._id}
                        component="option"
                      >
                        {item?.fromStopName} - {item?.toStopName} (
                        {item?.fromDistance} - {item?.toDistance} KMs)
                      </MenuItem>
                    ))}
                  </CustomSelectBox>
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextFieldLabel title={"Fee Structure"} required />
              <Controller
                name={"transportFeeDocId"}
                control={control}
                rules={{
                  required: "This field is required",
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <CustomSelectBox
                    native
                    displayEmpty
                    fullWidth
                    size="small"
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                  >
                    <MenuItem value="" component="option">
                      Select
                    </MenuItem>
                    {state?.feeStructureList?.length === 1 &&
                      state?.feeStructureList[0]?._id && (
                        <MenuItem
                          key={state.feeStructureList[0]?._id}
                          value={state.feeStructureList[0]?._id}
                          component="option"
                        >
                          {state.feeStructureList[0]?.feeCode} -{" "}
                          {`${IndianCurrency(
                            state.feeStructureList[0]?.feeAmount
                          )}`}
                        </MenuItem>
                      )}
                  </CustomSelectBox>
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextFieldLabel title={"To Stop Name"} />
              <Controller
                name={"to"}
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <CustomSelectBox
                    native
                    displayEmpty
                    fullWidth
                    size="small"
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                    disabled={!watch("rangeDocId")}
                  >
                    <MenuItem value="" component="option">
                      Select
                    </MenuItem>
                    {(stopsResponse?.data?.list || []).map((stop, i) => (
                      <MenuItem
                        value={stop.stopName}
                        key={i}
                        component="option"
                      >
                        {stop.stopName}
                      </MenuItem>
                    ))}
                  </CustomSelectBox>
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextFieldLabel title={"Transport Vehicle"} />
              <Controller
                name={"vehicleNumber"}
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <CustomSelectBox
                    native
                    displayEmpty
                    fullWidth
                    size="small"
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                    disabled={!state.selectedRouteData?.assignedVehicle?.length}
                  >
                    <MenuItem value="" component="option">
                      Select
                    </MenuItem>
                    {(state.selectedRouteData?.assignedVehicle || []).map(
                      (vehicle, i) => (
                        <MenuItem
                          value={vehicle.vehicleNumber}
                          key={i}
                          component="option"
                        >
                          {vehicle.vehicleNumber}
                        </MenuItem>
                      )
                    )}
                  </CustomSelectBox>
                )}
              />
            </Grid>

            {/* Stop Lists Details - Only show when range is selected in range mode */}
            {searchFeeBy === "range" && watch("rangeDocId") && (
              <Grid item xs={12} md={12}>
                <Stack direction={"row"} gap={1} alignItems={"center"}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                    Stops :-{" "}
                  </Typography>
                  {(stopsResponse?.data?.list || []).map((stop, i) => (
                    <React.Fragment key={i}>
                      <span style={{ fontSize: "12px", fontWeight: 500 }}>
                        {stop.stopName} ({stop.distance}KM)
                      </span>
                      {(stopsResponse?.data?.list || []).length !== i + 1 && (
                        <span>
                          <ArrowRight color="royalblue" size={20} />
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </Stack>
              </Grid>
            )}

            <Grid item xs={6} md={6}>
              <TextFieldLabel title={"Remarks"} />
              <Controller
                name={"remarks"}
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <CustomTextField
                    fullWidth
                    size="small"
                    onBlur={onBlur}
                    onChange={onChange}
                    placeholder="Type Remarks"
                    value={value}
                  />
                )}
              />
            </Grid>
          </>
        )}
        <Grid item xs={6} md={watch("using") === "Yes" ? 6 : 3}>
          <TextFieldLabel title={"Start Date"} required />
          <Controller
            name={"startDate"}
            control={control}
            rules={{
              required: "This field is required",
            }}
            render={({ field: { onChange, value } }) => (
              <DesktopDatePicker
                inputFormat="DD MMM, YYYY"
                onChange={onChange}
                value={value}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    size="small"
                    fullWidth
                    error={!!errors?.startDate}
                  />
                )}
              />
            )}
          />
        </Grid>
      </>
    );
  };

  const ByStops = () => {
    return (
      <>
        <Grid item xs={12} md={6}>
          <TextFieldLabel title={"Using"} required />
          <CustomAutoCompleteController
            name={"using"}
            control={control}
            options={["Yes", "No"]}
            placeholder={"Select Using"}
            rules={{
              required: "This field is required",
            }}
          />
        </Grid>

        {watch("using") === "Yes" && (
          <>
            <Grid item xs={12} md={6}>
              <TextFieldLabel title={"Search Stop"} required />
              <Box sx={{ position: "relative" }}>
                <CustomTextField
                  fullWidth
                  size="small"
                  placeholder="Type stop name to search..."
                  value={state.stopSearchText}
                  onChange={(e) => {
                    setState((prev) => ({
                      ...prev,
                      stopSearchText: e.target.value,
                      stopSearchLoading: true,
                      selectedStopData: null,
                    }));
                  }}
                  InputProps={{
                    endAdornment: state.stopSearchLoading && (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Search Results Dropdown */}
                {state.stopSearchResults.length > 0 &&
                  state.stopSearchText &&
                  !state.selectedStopData && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 1300,
                        backgroundColor: "white",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        marginTop: "4px",
                        maxHeight: "200px",
                        overflowY: "auto",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    >
                      <List dense>
                        {state.stopSearchResults.map((stop, index) => (
                          <ListItem key={index} disablePadding>
                            <ListItemButton
                              onClick={() => {
                                setState((prev) => ({
                                  ...prev,
                                  stopSearchText: stop.stopName,
                                  selectedStopData: stop,
                                  stopSearchResults: [],
                                }));
                              }}
                            >
                              <ListItemText
                                primaryTypographyProps={{
                                  sx: { fontSize: "1.05rem", fontWeight: 500 },
                                }}
                                primary={stop.stopName}
                                secondary={
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      sx={{ fontSize: "0.95rem" }}
                                    >
                                      Route: {stop.routeObject?.routeName} (
                                      {stop.routeObject?.routeNumber})
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      sx={{ fontSize: "0.95rem" }}
                                    >
                                      Range: {stop.rangeObject?.fromStopName}{" "}
                                      <East
                                        sx={{
                                          fontSize: 8,
                                        }}
                                      />{" "}
                                      {stop.rangeObject?.toStopName}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      sx={{ fontSize: "0.95rem" }}
                                    >
                                      Fee:{" "}
                                      {IndianCurrency(
                                        stop.feeStructureObject?.feeAmount
                                      )}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                {/* No results message */}
                {state.stopSearchText.length >= 2 &&
                  !state.stopSearchLoading &&
                  state.stopSearchResults.length === 0 &&
                  !state.selectedStopData && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 1300,
                        backgroundColor: "white",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        marginTop: "4px",
                        padding: 2,
                        textAlign: "center",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    >
                      <Typography color="textSecondary" variant="body2">
                        No stops found. Try a different search term.
                      </Typography>
                    </Box>
                  )}
              </Box>
            </Grid>

            {state.selectedStopData && (
              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Selected Stop Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Stop Name:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {state.selectedStopData.stopName}
                      </Typography>
                    </Grid>
                    <Grid item xs={1}>
                      <Typography variant="body2" color="textSecondary">
                        Fee Amt.:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {IndianCurrency(
                          state.selectedStopData.feeStructureObject?.feeAmount
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Route:
                      </Typography>
                      <Typography variant="body1">
                        {state.selectedStopData.routeObject?.routeName} (
                        {state.selectedStopData.routeObject?.routeNumber})
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Range:
                      </Typography>
                      <Typography variant="body1">
                        {state.selectedStopData.rangeObject?.fromStopName}{" "}
                        <East
                          sx={{
                            fontSize: 8,
                          }}
                        />{" "}
                        {state.selectedStopData.rangeObject?.toStopName}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="body2" color="textSecondary">
                        Vehicle:
                      </Typography>
                      <Typography variant="body1">
                        {state.selectedStopData.vehicleObject?.vehicleNumber} (
                        {state.selectedStopData.vehicleObject?.vehicleNickName})
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}

            <Grid item xs={6} md={6}>
              <TextFieldLabel title={"Remarks"} />
              <Controller
                name={"remarks"}
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <CustomTextField
                    fullWidth
                    size="small"
                    onBlur={onBlur}
                    onChange={onChange}
                    placeholder="Type Remarks"
                    value={value}
                  />
                )}
              />
            </Grid>
          </>
        )}
        <Grid item xs={6} md={6}>
          <TextFieldLabel title={"Start Date"} required />
          <Controller
            name={"startDate"}
            control={control}
            rules={{
              required: "This field is required",
            }}
            render={({ field: { onChange, value } }) => (
              <DesktopDatePicker
                inputFormat="DD MMM, YYYY"
                onChange={onChange}
                value={value}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    size="small"
                    fullWidth
                    error={!!errors?.startDate}
                  />
                )}
              />
            )}
          />
        </Grid>
      </>
    );
  };

  const handleAddTransportStudentMap = async (data) => {
    let payload = {};
    if (searchFeeBy === "feeStructure") {
      payload = {
        studentDocId: data?.studentDocId,
        session: selectedSession,
        transportInfo: {
          using: data?.using,
          ...(data?.using === "Yes"
            ? {
                transportFeeDocId: data?.transportFeeDocId,
                feeAmount: data?.feeAmount,
                startDate: data?.startDate,
                remarks: data?.remarks,
              }
            : {
                transportFeeDocId: "",
                feeAmount: "",
                startDate: data?.startDate,
                remarks: "",
              }),
        },
      };
    }
    if (searchFeeBy === "range") {
      payload = {
        studentDocId: data?.studentDocId,
        session: selectedSession,
        transportInfo: {
          using: data?.using,
          ...(data?.using === "Yes"
            ? {
                routeNumber: data?.routeNumber,
                rangeCode: data?.rangeCode,
                // routeDocId: data?.routeDocId,
                // rangeDocId: data?.rangeDocId,
                to: data?.to,
                vehicleNumber: data?.vehicleNumber,
                transportFeeDocId: data?.transportFeeDocId,
                feeAmount: data?.feeAmount,
                startDate: data?.startDate,
                remarks: data?.remarks,
              }
            : {
                routeNumber: "",
                rangeCode: "",
                // routeDocId: data?.routeDocId,
                // rangeDocId: data?.rangeDocId,
                to: "",
                vehicleNumber: "",
                transportFeeDocId: "",
                feeAmount: "",
                startDate: data?.startDate,
                remarks: "",
              }),
        },
      };
    }
    if (searchFeeBy === "stops") {
      payload = {
        studentDocId: data?.studentDocId,
        session: selectedSession,
        transportInfo: {
          using: data?.using,
          ...(data?.using === "Yes" && state.selectedStopData
            ? {
                routeNumber: state.selectedStopData.routeObject?.routeNumber,
                rangeCode: state.selectedStopData.rangeObject?.rangeCode,
                to: state.selectedStopData.stopName,
                vehicleNumber:
                  state.selectedStopData.vehicleObject?.vehicleNumber,
                transportFeeDocId:
                  state.selectedStopData.feeStructureObject?.transportFeeDocId,
                feeAmount: state.selectedStopData.feeStructureObject?.feeAmount,
                startDate: data?.startDate,
                remarks: data?.remarks,
              }
            : {
                routeNumber: "",
                rangeCode: "",
                to: "",
                vehicleNumber: "",
                transportFeeDocId: "",
                feeAmount: "",
                startDate: data?.startDate,
                remarks: "",
              }),
        },
      };
    }
    mutate(payload);
  };

  return (
    <>
      <CommonDialog
        open={dialog}
        onClose={() => {
          setState((prev) => ({
            ...prev,
            byNameStudentData: {},
          }));
          onClose();
        }}
        minWidth="900px"
        title={"Transport Detail"}
      >
        <DialogContent>
          <form
            id="add-transport-student-map"
            onSubmit={handleSubmit(handleAddTransportStudentMap)}
          >
            <Box>
              <Grid container spacing={2}>
                {showStudentSearch && (
                  <Grid item xs={6} md={6}>
                    <StudentSearchDialogForSiblings
                      handleOnClickStudent={(data) => {
                        setState((prev) => ({
                          ...prev,
                          byNameStudentData: data,
                        }));
                        reset((prev) => ({
                          ...prev,
                          studentDocId: data?.studentDocId,
                        }));
                      }}
                      value={"Student Name"}
                      radio={"activeSession"}
                    />
                    {watch("studentDocId") === "" && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "12px", ml: 1 }}
                      >
                        This field is required
                      </Typography>
                    )}
                  </Grid>
                )}
                <Grid item xs={6} md={6}>
                  {(state?.byNameStudentData?.studentDocId ||
                    editData?.studentDocId?._id) && (
                    <Stack
                      sx={{
                        border: "1px solid #3D398933",
                        backgroundColor: "#B6C8D620",
                        width: "100%",
                      }}
                    >
                      <StudentCard
                        item={state?.byNameStudentData}
                        imageSize="50px"
                        cardWidth="100%"
                      />
                    </Stack>
                  )}
                </Grid>
                <Grid item xs={12} md={12}>
                  <Stack direction={"row"} spacing={1} alignItems={"center"}>
                    <TextFieldLabel title={"Search Fee By"} />
                    <FormControlLabel
                      control={
                        <Radio
                          checked={searchFeeBy === "stops"}
                          onChange={() => {
                            setSearchFeeBy("stops");
                          }}
                        />
                      }
                      label="By Stops"
                    />

                    <FormControlLabel
                      control={
                        <Radio
                          checked={searchFeeBy === "feeStructure"}
                          onChange={() => {
                            setSearchFeeBy("feeStructure");
                          }}
                        />
                      }
                      label="By Fee Structure"
                    />

                    <FormControlLabel
                      control={
                        <Radio
                          checked={searchFeeBy === "range"}
                          onChange={() => {
                            setSearchFeeBy("range");
                          }}
                        />
                      }
                      label="By Range"
                    />
                  </Stack>
                </Grid>

                {searchFeeBy === "range" && ByRange()}
                {searchFeeBy === "feeStructure" && ByFeeStructure()}
                {searchFeeBy === "stops" && ByStops()}
              </Grid>
            </Box>
          </form>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setState((prev) => ({
                ...prev,
                byNameStudentData: {},
              }));
              onClose();
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            type="submit"
            form="add-transport-student-map"
            loading={updatingTranpsortInfoLoading}
            disabled={
              !watch("studentDocId") ||
              (searchFeeBy === "range" &&
                watch("using") === "Yes" &&
                (!watch("routeDocId") || !watch("rangeDocId"))) ||
              (searchFeeBy === "feeStructure" &&
                watch("using") === "Yes" &&
                (!watch("transportFeeDocId") ||
                  !watch("startDate") ||
                  !watch("using"))) ||
              (searchFeeBy === "stops" &&
                watch("using") === "Yes" &&
                (!state.selectedStopData || !watch("startDate")))
            }
          >
            Submit
          </LoadingButton>
        </DialogActions>
      </CommonDialog>
    </>
  );
};

export default AddTransportStudentMap;
